/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.effects

import io.peekandpoke.klang.audio_be.AudioBuffer
import io.peekandpoke.klang.audio_be.effects.Compressor.Companion.DB20_OVER_LN10
import io.peekandpoke.klang.audio_be.effects.Compressor.Companion.ENV_COEFF_BLEND_DB
import io.peekandpoke.klang.audio_be.effects.Compressor.Companion.FAST_RELEASE_DIVISOR
import io.peekandpoke.klang.audio_be.effects.Compressor.Companion.LN10_OVER_20
import io.peekandpoke.klang.audio_be.utils.copyRangeInto
import io.peekandpoke.klang.audio_be.utils.fastExp
import io.peekandpoke.klang.audio_be.utils.finiteOrZero
import io.peekandpoke.klang.audio_be.utils.isFiniteInline
import io.peekandpoke.klang.audio_be.utils.timeConstantCoeff
import kotlin.math.abs
import kotlin.math.ln
import kotlin.math.max

/**
 * Dynamic range compressor with configurable threshold, ratio, knee, attack, and release.
 *
 * This compressor reduces the dynamic range of audio by attenuating signals above the threshold.
 * The amount of attenuation is controlled by the ratio parameter.
 *
 * **Two topologies, selected by [lookaheadSeconds]:**
 *   - `0` (default) — dB-domain one-pole envelope follower (peak link via `max(|L|, |R|)`) feeding a
 *     soft-knee parabolic gain curve. Standard musical-compressor design; no added latency.
 *   - `> 0` — anticipating: running MINIMUM of the required gain over the lookahead window, a
 *     release stage, then two cascaded box filters, applied to the signal delayed by that window.
 *     Holds the ceiling on transients instead of chasing them. Costs exactly that much latency: on
 *     the summed mix (the house limiter) that shifts everything together, anywhere else it makes
 *     whatever runs through it late by that much (an orbit's `Katalyst(k => k.limiter(lookahead =
 *     ...))`, phase 3 step 12 C2: the author's choice, never compensated).
 *
 * Used in two places:
 *   1. Per-cylinder musical compressor (user-facing).
 *   2. Master brickwall limiter via `KlangAudioRenderer.kt:20-27`.
 *
 * **Round 5 review (2026-04-30)** kept the topology and applied a code-quality cleanup pass.
 * Deferred-but-known items live in the user's memory at
 * `~/.claude/projects/-opt-dev-peekandpoke-klang/memory/project_filter_review.md`:
 *   - Linear-domain rewrite (would eliminate 2 `exp`/`ln` per sample on top of the constant
 *     caching done here; deferred to the future dedicated `MasterLimiter` work).
 *   - Peak-detector RMS smoothing (secondary britzeling fix; deferred).
 *   - ~~Lookahead support~~ — DONE 2026-08-06, see [lookaheadSeconds] and `lookaheadStep`.
 *
 * @param sampleRate Sample rate in Hz
 * @param thresholdDb Threshold in dB (default: -20.0). Signals above this are compressed.
 * @param ratio Compression ratio (default: 4.0). For example, 4:1 means 4 dB input = 1 dB output above threshold.
 * @param kneeDb Soft knee width in dB (default: 6.0). A wider knee makes the compression more gradual.
 *   `kneeDb = 0` produces a hard-knee gain curve with a C¹ slope kink at the threshold corner;
 *   audible as crackle/sizzle on transient-rich material. Use ≥ 1 dB on brickwall settings.
 * @param attackSeconds Attack time in seconds (default: 0.003). How quickly compression is applied.
 * @param releaseSeconds Release time in seconds (default: 0.1). How quickly compression is released.
 */
class Compressor(
    private val sampleRate: Int,
    thresholdDb: Double = -20.0,
    ratio: Double = 4.0,
    kneeDb: Double = 6.0,
    attackSeconds: Double = 0.003,
    releaseSeconds: Double = 0.1,
    /**
     * Lookahead in seconds. `0.0` (the default) keeps the classic feed-forward one-pole path with
     * no delay line and no added latency, which is what an orbit compressor uses unless its author
     * asks for more.
     *
     * Above [MIN_LOOKAHEAD_FRAMES] worth of samples this switches to the anticipating path, which
     * delays the signal by this much. On the summed mix (the house limiter in `MasterStage`) the
     * delay is uniform for everything downstream; on an orbit (a Katalyst `compressor` or `limiter`
     * stage with a `lookahead`) that orbit runs late by it, by the author's choice and uncompensated.
     *
     * A constructor `val`, deliberately, unlike every other parameter here: the rings are sized once
     * from it and must never be reallocated on the audio thread. A caller that takes it from a user
     * passes it through [coerceLookaheadSeconds] first, the one bound on what a build allocates.
     */
    val lookaheadSeconds: Double = 0.0,
) {
    // The five knobs as stored: the properties below read and write them, and [applyKnobArgs] writes them directly.
    private var thresholdValue: Double = guardOr(value = thresholdDb, fallback = -20.0)
    private var ratioValue: Double = guardOr(value = ratio, fallback = 4.0).coerceAtLeast(1.0)
    private var kneeValue: Double = guardOr(value = kneeDb, fallback = 6.0).coerceAtLeast(0.0)
    private var attackValue: Double = guardOr(value = attackSeconds, fallback = 0.003)
    private var releaseValue: Double = guardOr(value = releaseSeconds, fallback = 0.1)

    // Compressor parameters (all mutable for real-time changes; setters silently ignore non-finite values).
    var thresholdDb: Double
        get() = thresholdValue
        set(value) {
            if (!value.isFinite()) return
            thresholdValue = value
            updateCoefficients()
        }

    var ratio: Double
        get() = ratioValue
        set(value) {
            if (!value.isFinite()) return
            ratioValue = value.coerceAtLeast(1.0)
            updateCoefficients()
        }

    var kneeDb: Double
        get() = kneeValue
        set(value) {
            if (!value.isFinite()) return
            kneeValue = value.coerceAtLeast(0.0)
            updateCoefficients()
        }

    /**
     * How fast the gain closes. One concept, two mechanisms: a one-pole time constant when
     * [lookaheadSeconds] is 0, the smoothing length when it is not.
     *
     * On the lookahead path this re-sizes the two box filters ([resizeSmoothing]): CONTINUOUSLY, the
     * running sums take or drop the samples entering or leaving each window, and nothing is re-primed.
     * It allocates nothing (the rings are sized once at construction) and the ceiling holds for any
     * old/new pair of lengths. So it may be written per block: a modulated attack (Katalyst knob
     * expressions step 5b) moves the smoothing length by a tap or two per change, and the gain moves by
     * about a tap's share of the window, not by a step. Measured (DC bursts, threshold -20, ratio 10,
     * lookahead 5 ms): the largest one-sample gain step under an attack moving 2 to 4 ms per block is
     * within a small factor of a fixed 2 ms attack's (`LimiterLookaheadSpec`), where the re-priming this
     * replaced stepped it by 0.6 to 0.7 of an 0.87 reduction (review round 1 of step 5b).
     */
    var attackSeconds: Double
        get() = attackValue
        set(value) {
            if (!value.isFinite()) return
            attackValue = value
            updateCoefficients()
            if (delayFrames > 0) resizeSmoothing()
        }

    var releaseSeconds: Double
        get() = releaseValue
        set(value) {
            if (!value.isFinite()) return
            releaseValue = value
            updateCoefficients()
        }

    /**
     * The five knobs of one [applyKnobArgs], written by the caller before the call, indexed by [KNOB_THRESHOLD] ...
     * [KNOB_RELEASE]: the raw values, as a caller would hand them to the five setters.
     *
     * **A holder, on purpose** (engine follow-up 40, 2026-10-16): the orbit compressor writes all five knobs on every
     * block one of them moves. Through the five setters each knob crossed a call V8 does not inline, so each was a
     * heap number (`audio/ref/performance.md`, the rule on a double handed to a function that is not inlined), and
     * each setter recomputed the three time constants, fifteen `exp` per block where three do. An ARRAY, not double
     * fields: an element access in optimized code never allocates (engine follow-up 7), as for [glideArgs]. No test
     * can pin the box; this KDoc and the allocation check's `E-kat-dyn` line are the guard.
     */
    internal val knobArgs = DoubleArray(KNOB_ARGS)

    /**
     * Applies [knobArgs] as the five setters would, called in the order threshold, ratio, knee, attack, release, and
     * stores the same numbers bit for bit: each knob guarded and coerced as its setter does (a non-finite one keeps
     * the stored value), then the time constants computed ONCE. That is exact because nothing between the setters
     * reads what an earlier one computed: the coefficients are a function of the stored attack and release alone, so
     * the last of five recomputations is the only one that survives, and the lookahead's smoothing re-split
     * ([resizeSmoothing]) reads only the stored attack and runs, as in the attack setter, only when the attack was
     * written. Guarded by "applyKnobArgs leaves the instance where the five setters leave it, bit for bit"
     * (`CompressorSpec`).
     */
    internal fun applyKnobArgs() {
        val args = knobArgs
        val threshold = args[KNOB_THRESHOLD]
        val ratio = args[KNOB_RATIO]
        val knee = args[KNOB_KNEE]
        val attack = args[KNOB_ATTACK]
        val release = args[KNOB_RELEASE]

        // `isFiniteInline`, not the stdlib `isFinite()` (a call on Kotlin/JS): the same answer for every input.
        if (threshold.isFiniteInline()) {
            thresholdValue = threshold
        }

        // The setters' `coerceAtLeast`, written out (the stdlib's body), so no double crosses a call.
        if (ratio.isFiniteInline()) {
            ratioValue = if (ratio < 1.0) 1.0 else ratio
        }

        if (knee.isFiniteInline()) {
            kneeValue = if (knee < 0.0) 0.0 else knee
        }

        val attackWritten = attack.isFiniteInline()

        if (attackWritten) {
            attackValue = attack
        }

        if (release.isFiniteInline()) {
            releaseValue = release
        }

        updateCoefficients()

        if (attackWritten && delayFrames > 0) {
            resizeSmoothing()
        }
    }

    // Envelope follower state.
    private var envelopeDb: Double = SILENCE_DB

    /**
     * True when the knobs at the `TO` ends of [glideArgs] reduce LESS than those at the `FROM` ends, both on this curve
     * at the fixed reference input [KNOB_MOVE_REFERENCE_DB] (0 dBFS), never at the follower's envelope. The orbit
     * compressor's knob move asks it once per new move to pick its law (`CompressorKnobMove`): the six knobs travel in
     * the holder, the answer is a Boolean, so no double crosses a call.
     *
     * STRICT on purpose: a tie glides (both thresholds above 0 dBFS reduce nothing there; a knee-only change far below
     * the reference is the same line).
     */
    internal fun reducesLessAtGlideEnds(): Boolean {
        val a = glideArgs
        val env = KNOB_MOVE_REFERENCE_DB
        val now = gainReductionDb(
            inputDb = env,
            thresholdDb = a[GLIDE_THRESHOLD_FROM],
            slope = a[GLIDE_INVERSE_RATIO_FROM] - 1.0,
            kneeDb = a[GLIDE_KNEE_FROM],
        )
        val then = gainReductionDb(
            inputDb = env,
            thresholdDb = a[GLIDE_THRESHOLD_TO],
            slope = a[GLIDE_INVERSE_RATIO_TO] - 1.0,
            kneeDb = a[GLIDE_KNEE_TO],
        )

        return then > now
    }

    // Computed coefficients (refreshed on any parameter setter).
    private var attackCoeff: Double = 0.0
    private var releaseCoeff: Double = 0.0
    private var fastReleaseCoeff: Double = 0.0

    // ── Lookahead state ──────────────────────────────────────────────────────
    // Sized once, here. Nothing below is ever reallocated: `lookaheadSeconds` is a
    // constructor val precisely so the ring cannot be resized on the audio thread.

    /** Signal delay D in frames. 0 disables the whole lookahead path. */
    private val delayFrames: Int = run {
        // guardOr like every sibling param: Infinity would saturate .toInt() to Int.MAX_VALUE and
        // ask for a 2-billion-element array. KatalystCompressorEffect already guards, but this class is public
        // with two other call sites (MasterStage and EffectBenchmark).
        val seconds = guardOr(value = lookaheadSeconds, fallback = 0.0)
        val frames = (seconds * sampleRate).toInt()
        if (frames < MIN_LOOKAHEAD_FRAMES) 0 else frames
    }

    /** Signal delay rings (one per channel, one shared write index). */
    private val delayL = DoubleArray(if (delayFrames > 0) delayFrames else 0)
    private val delayR = DoubleArray(if (delayFrames > 0) delayFrames else 0)

    /** Frames of signal delay this instance actually adds — 0 on the classic path. */
    val latencyFrames: Int get() = delayFrames

    private var delayPos = 0

    // Monotonic-increasing deque for the running MINIMUM of the required gain over a window of
    // `delayFrames + 1`. Primitive-backed: ArrayDeque<Double> boxes on Kotlin/JS.
    // Sized delayFrames + 3, and the +3 is load-bearing. The window holds up to `delayFrames + 1`
    // entries, but eviction runs AFTER the push, so occupancy transiently reaches `delayFrames + 2`
    // — and a head/tail ring represents at most `size - 1`. At `size = delayFrames + 2` that push
    // makes tail meet head, the deque reads as EMPTY, the emptiness-guarded eviction then does
    // nothing, and the next push overwrites the head: the whole hold window is discarded and the
    // ceiling guarantee with it. Reproduced on monotonically-falling loud sub-bass (a saturated
    // 20 Hz tone at +18 dB over threshold leaked 362 samples past 1.0).
    //
    // ⚠️ The `+ 3` and the `minHead != minTail` guard in the eviction loop are ONE fix, not two.
    // Without the guard, `size = delayFrames + 2` is exactly correct — the ambiguous full-ring state
    // self-corrects by evicting one stale entry. The guard (added for Int-wrap safety) is what makes
    // the extra slot load-bearing. Do not "simplify" either half on the grounds that the other
    // handles it.
    private val minVals = DoubleArray(if (delayFrames > 0) delayFrames + 3 else 0)
    private val minIdx = IntArray(if (delayFrames > 0) delayFrames + 3 else 0)
    private var minHead = 0
    private var minTail = 0
    private var sampleCounter = 0

    // Two cascaded box filters: combined support b1 + b2 - 1 taps, coverage condition
    // b1 + b2 - 2 <= delayFrames.
    // Allocated ONCE at the ceiling (the CAPACITY, `(delayFrames + 2) / 2`); `boxLen` is the window in use.
    // Each ring keeps the last `capacity` values it was fed, indexed modulo its capacity whatever
    // `boxLen` is, so a length change only moves the window over history the ring already holds
    // (see resizeSmoothing). Nothing is allocated or resized on the audio thread.
    private val boxA = DoubleArray(if (delayFrames > 0) (delayFrames + 2) / 2 else 0)
    private val boxB = DoubleArray(if (delayFrames > 0) (delayFrames + 2) / 2 else 0)
    private var boxLen = 1
    private var boxAPos = 0
    private var boxBPos = 0
    private var boxASum = 0.0
    private var boxBSum = 0.0

    /**
     * Release-stage state — a **dual, program-dependent release**. Runs BEFORE the smoother, and
     * only on the lookahead path (the classic path keeps its own single release in [envelopeStep]).
     *
     * [slowGain] tracks the required gain with a plain one-pole in both directions: it absorbs the
     * sustained level and barely moves. [fastGain] handles only what is left over (`held / slow`),
     * instant down and one-pole up, so it deals with each individual hit and is finished before the
     * next one lands. Applied gain is their product, which is `<= held` by construction — so the
     * ceiling argument in [lookaheadStep]'s KDoc is unaffected.
     *
     * The fast branch is `releaseSeconds` / [FAST_RELEASE_DIVISOR]. Deliberately derived rather than
     * exposed: `releaseSeconds` keeps meaning "the release", and no new DSL knob was added for
     * something nobody had asked to control.
     *
     * ── How we got here (2026-08-06/07), because the reasoning is not obvious from the code ──
     *
     * Adding lookahead fixed the transient "knock" but created a new problem: the limiter went from
     * doing ~nothing during a transient (the hard clip did the work) to applying its full reduction
     * to the **entire summed mix** on every hit. With a single 100 ms release the gain is still
     * climbing back when the next kick lands at a musical tempo, so a sustained bed audibly swells
     * between hits — a pump. Measured on a kick-plus-bed at 120 BPM: the bed sat 0.67 dB down
     * between kicks and the mix ran 1.59 dB below its ceiling.
     *
     * Two other fixes were measured and REJECTED, so nobody re-tries them:
     *  - **A dB-domain release.** Its gap is also exponential, so it crawls in the last dB just as
     *    badly: 205 ms to recover within 0.5 dB, versus 187 ms for the linear one. No better.
     *  - **Simply shortening the release.** It does reduce the pump, but it gives back most of what
     *    the 5 ms lookahead window bought: 55 Hz non-fundamental energy goes −41.1 dB → −37.4 dB at
     *    a 50 ms release, −35.3 dB at 30 ms.
     *
     * ⚠️ **The by-ear verdict on the pump itself was "really subtle"** — and that was on material
     * built to be maximally unkind (a sustained saw pad under a hard four-on-the-floor at
     * a master gain of 2.2, level-matched A/B). On real songs it is likely inaudible. **So this is
     * NOT kept for the pumping.**
     *
     * It is kept because of the second, non-subtle consequence: at an identical peak (−0.34 dBFS in
     * both renders) the mean level goes **−1.59 dB → −0.55 dB**. Same ceiling, ~1 dB louder, because
     * the mix spends less time ducked. That is roughly one gain stage's worth of loudness for one
     * divide per sample, on a single instance — and loudness at a fixed ceiling is exactly what a
     * master chain exists to produce.
     *
     * If this ever needs to be reverted: the pump alone does not justify it, and dropping back to a
     * single release costs ~1 dB of loudness, not audible quality.
     */
    private var slowGain = 1.0
    private var fastGain = 1.0

    init {
        updateCoefficients()
        if (delayFrames > 0) {
            resizeSmoothing()
            resetLookaheadState()
        }
    }

    /**
     * Re-split the box taps from [attackSeconds], honouring `b1 + b2 - 2 <= delayFrames`.
     *
     * **Continuous, no re-prime** (review round 1 of knob expressions step 5b). Each ring holds the
     * last `capacity` values it was fed (written modulo its capacity, never modulo [boxLen]), so the
     * new window's samples are already there: a longer box adds the samples between the old and the
     * new window to its running sum, a shorter one subtracts the samples that leave it. The output is
     * then the mean over the new window of what the box was really fed, so it moves by the difference
     * of two means over overlapping windows, never jumps to the instantaneous gain. Re-priming here,
     * as before, refilled both rings with the current gain and stepped the applied gain by up to the
     * whole reduction in one sample under a modulated attack (0.6 to 0.7 of an 0.87 reduction, measured).
     *
     * **Safe for any old/new pair.** Every box length is at most the capacity `C = (delayFrames + 2) / 2`,
     * so for ANY two lengths `b1, b2 <= C`, `b1 + b2 - 2 <= 2C - 2 <= delayFrames`: whatever lengths
     * the two boxes ran at while a given output sample was being smoothed, every release-stage value it
     * averages lies inside that sample's hold window, which is the ceiling argument of [lookaheadStep].
     * Values written before the last prime (construction, [reset], [reseedFromRing]) are the prime's
     * seed: reachable only by a window reaching back past the prime, which the same bound keeps to
     * samples whose delayed output is the ring's silence (construction, reset) or already re-heard
     * (reseed: `delayFrames` steps follow the prime).
     *
     * **Allocates nothing**; the adjustment costs one read per sample entering or leaving a window
     * (a tap or two per change for a modulated attack, at most `C` once for a jump across the range).
     * At a FIXED length nothing here runs and the per-sample sums are added and subtracted in the
     * order they always were, so a fixed attack (the house limiter) renders bit for bit as before.
     */
    private fun resizeSmoothing() {
        val requested = (attackValue * sampleRate).toInt()
        val total = requested.coerceIn(2, delayFrames + 2)
        // Floor the per-box tap count. At `half == 1` the two boxes are an identity — no smoothing
        // at all — and the gain steps by the full reduction in a single sample (~595 dB/ms), which
        // is exactly the click MIN_LOOKAHEAD_FRAMES exists to prevent. Reachable from the DSL as
        // `.lookahead(0.005).attack(0.0)`, i.e. from "make it snappy". The zero-lookahead path has
        // the equivalent floor in updateCoefficients() (`max(MIN_TIME_SECONDS, attackValue)`); this is its
        // twin, and a bound against a defect rather than a clamp on taste.
        // NB `boxA.size` is (delayFrames + 2) / 2, and delayFrames >= MIN_LOOKAHEAD_FRAMES, so the
        // range cannot invert. That coupling is MIN_LOOKAHEAD_FRAMES >= 2 * MIN_BOX_TAPS - 2 — break
        // it and coerceIn throws IllegalArgumentException on the audio thread.
        val half = (total / 2).coerceIn(MIN_BOX_TAPS, boxA.size)

        if (half == boxLen) {
            return
        }

        boxASum += windowChange(ring = boxA, pos = boxAPos, from = boxLen, to = half)
        boxBSum += windowChange(ring = boxB, pos = boxBPos, from = boxLen, to = half)
        boxLen = half
    }

    /**
     * What a box's running sum gains when its window goes from [from] to [to] samples: the sum of the
     * samples written `from + 1 .. to` samples ago for a longer window, minus those written
     * `to + 1 .. from` ago for a shorter one. [pos] is where the ring writes next, so the sample written
     * `k` samples ago sits at `pos - k`, modulo the capacity. Inline, so no double crosses a call.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun windowChange(ring: DoubleArray, pos: Int, from: Int, to: Int): Double {
        val capacity = ring.size
        val sign = if (to > from) 1.0 else -1.0
        val shorter = if (to > from) from else to
        val longer = if (to > from) to else from
        var change = 0.0

        for (k in shorter + 1..longer) {
            val at = pos - k

            change += ring[if (at < 0) at + capacity else at]
        }

        return sign * change
    }

    /**
     * Refill both box rings, their whole capacity, with the CURRENT gain and rebuild their running
     * sums for [boxLen]. For construction and the detector resets ([resetLookaheadGain]), never for a
     * length change ([resizeSmoothing] moves the window continuously). The whole capacity, so a later
     * longer window finds the seed and not a stale value.
     *
     * Priming with `1.0` would be a full-scale event, not a neutral one: mid-stream at −12 dB of
     * reduction it snaps the gain to unity in a single sample (measured: Δgain 0.76 linear,
     * +7.42 dBFS out, 65 samples past the ceiling) and holds it open for ~2·boxLen samples while the
     * rings refill — the exact knock this whole feature removes. Priming with [releaseGain] is
     * continuous AND still safe: the applied gain `<= held = min(required[n−D…n])`, and a primed tap can
     * only affect samples within `boxLen − 1 <= D` of now, all inside that window.
     */
    private fun primeBoxes() {
        val seed = slowGain * fastGain

        boxA.fill(seed)
        boxB.fill(seed)
        boxAPos = 0
        boxBPos = 0
        boxASum = seed * boxLen
        boxBSum = seed * boxLen
    }

    /**
     * Process a stereo buffer in-place.
     */
    fun process(left: AudioBuffer, right: AudioBuffer, blockSize: Int) {
        // Two loop bodies, branched OUTSIDE the per-sample loop: `envelopeStep` is inlined into the
        // classic one so the zero-lookahead path stays exactly as specialized as it was. The
        // lookahead loop is [processLookahead]'s, with the block itself as the delayed-dry target
        // (every sample is read before it is written, so the aliasing is safe).
        if (delayFrames > 0) {
            processLookahead(left = left, right = right, blockSize = blockSize, delayedLeft = left, delayedRight = right)

            return
        }

        for (i in 0 until blockSize) {
            val totalGain = envelopeStep(max(abs(left[i]), abs(right[i])))
            left[i] = left[i] * totalGain
            right[i] = right[i] * totalGain
        }
    }

    /**
     * The six ends of a gliding block's ramps, written by the caller and read by [processGliding], indexed by
     * [GLIDE_THRESHOLD_FROM] ... [GLIDE_KNEE_TO].
     *
     * **A holder, on purpose** (Katalyst knob expressions step 6, 2026-10-12): a modulated compressor knob glides
     * every block, [processGliding] is past V8's 460-byte limit for inlining, so it is always a call, and on V8 a
     * non-integral double handed to a call that is not inlined is a heap number (`audio/ref/performance.md`): as six
     * arguments they cost about six heap numbers per block (mutant M7 of
     * `docs/benchmarks/2026-10-12_183400_knob-step6-v8.md`: `E-kat-dyn` 196.5 B against 98.7, `--predictable`,
     * single-case; 196.4 with the allocation check's method, over its line). An ARRAY, not double fields: an element
     * access in optimized code never allocates (engine follow-up 7). [DelayLine]'s `blockArgs` is the precedent. No
     * test can pin it; this KDoc and the allocation check's `E-kat-dyn` line are the guard.
     */
    internal val glideArgs = DoubleArray(GLIDE_ARGS)

    /**
     * One stereo block of the classic path while the three GAIN-COMPUTER knobs move: threshold,
     * inverse ratio and knee run linearly per sample from the values the previous block ended on
     * (`thresholdFrom`, `inverseRatioFrom`, `kneeFrom`) to `thresholdTo`, `inverseRatioTo` and
     * `kneeTo`, written from the END, so the last sample runs at the `To` values bit for bit. The six arrive in
     * [glideArgs], written by the caller before the call. The
     * envelope follower is the same one [process] runs (it reads none of the three), so it stays
     * continuous.
     *
     * **The ratio moves as its inverse, `1 / ratio`**, so the curve's slope `1 / ratio - 1` moves
     * linearly. A glide linear in the ratio itself bunches a wide change into one end of the glide
     * (from 100:1 to 1:1 the slope barely moves until the last few blocks): measured -41 dB above
     * 8 kHz for 100 to 1, where the inverse glide sits at the floor (5c-7 review round 1).
     *
     * Why per sample: the gain computer has no memory, so a knob that moved once per block would
     * step the gain once per block. Measured in Katalyst step 5c-7 (a band-limited saw, chord and
     * bass, energy above 8 kHz against the signal): a threshold, ratio or knee JUMP sits at -9 to
     * -52 dB, the same jump in 17 to 19 per-block steps at -31 to -69 dB (a model of this curve),
     * and a per-sample ramp at the steady floor. The orbit compressor's glide
     * (`KatalystCompressorEffect`) is the caller; the fields are NOT touched here, they hold what
     * the caller configured last.
     *
     * The values are the caller's and are used as given: pass what the setters stored (an inverse
     * ratio in `(0, 1]`, a knee of at least 0), and a linear ramp between two such values stays in
     * range.
     *
     * Classic path only: an instance with [lookaheadSeconds] runs [process] instead, on the knobs
     * in force, because its delay ring must see every block. There a knob step does not step the
     * output: the required gain passes through the hold, the release and the box smoother first.
     */
    internal fun processGliding(left: AudioBuffer, right: AudioBuffer, blockSize: Int) {
        if (delayFrames > 0 || blockSize <= 0) {
            process(left = left, right = right, blockSize = blockSize)

            return
        }

        val args = glideArgs
        val thresholdFrom = args[GLIDE_THRESHOLD_FROM]
        val thresholdTo = args[GLIDE_THRESHOLD_TO]
        val inverseRatioFrom = args[GLIDE_INVERSE_RATIO_FROM]
        val inverseRatioTo = args[GLIDE_INVERSE_RATIO_TO]
        val kneeFrom = args[GLIDE_KNEE_FROM]
        val kneeTo = args[GLIDE_KNEE_TO]

        val thresholdStep = (thresholdTo - thresholdFrom) / blockSize
        val inverseRatioStep = (inverseRatioTo - inverseRatioFrom) / blockSize
        val kneeStep = (kneeTo - kneeFrom) / blockSize
        val last = blockSize - 1

        for (i in 0 until blockSize) {
            val back = (last - i).toDouble()

            followEnvelope(max(abs(left[i]), abs(right[i])))

            val reductionDb = gainReductionDb(
                inputDb = envelopeDb,
                thresholdDb = thresholdTo - thresholdStep * back,
                slope = (inverseRatioTo - inverseRatioStep * back) - 1.0,
                kneeDb = kneeTo - kneeStep * back,
            )
            val totalGain = gainFor(reductionDb)

            left[i] = left[i] * totalGain
            right[i] = right[i] * totalGain
        }
    }

    /**
     * The lookahead loop, the ONE loop of the lookahead path ([process] runs it too, with the block
     * as its own target), which also hands out the DELAYED DRY signal: [delayedLeft] and
     * [delayedRight] receive, per sample, what leaves the delay ring before the gain is applied, so
     * `left[i] == delayedLeft[i] * gain`. That is the fade partner of an orbit compressor with
     * lookahead (`KatalystCompressorEffect`): blending the compressed signal against the UNDELAYED
     * input would sum two copies D frames apart, and landing on it would jump the orbit in time.
     *
     * [delayedLeft] and [delayedRight] may alias [left] and [right]: each sample is read before
     * either is written.
     *
     * On an instance without lookahead there is no ring: the delayed dry is the input itself, and
     * the block runs [process].
     */
    internal fun processLookahead(
        left: AudioBuffer,
        right: AudioBuffer,
        blockSize: Int,
        delayedLeft: AudioBuffer,
        delayedRight: AudioBuffer,
    ) {
        if (delayFrames <= 0) {
            left.copyRangeInto(destination = delayedLeft, destinationOffset = 0, startIndex = 0, endIndex = blockSize)
            right.copyRangeInto(destination = delayedRight, destinationOffset = 0, startIndex = 0, endIndex = blockSize)
            process(left = left, right = right, blockSize = blockSize)

            return
        }

        for (i in 0 until blockSize) {
            val l = left[i]
            val r = right[i]
            val gain = lookaheadStep(max(abs(l), abs(r)))
            // Emit the DELAYED sample, then park the current one. One shared write index.
            val outL = delayL[delayPos]
            val outR = delayR[delayPos]

            // NaN-guard on the ring write too, not just the detector: MasterStage's clip maps NaN
            // to -1.0 (full-scale negative), so an unguarded NaN would surface as a click
            // delayFrames samples later, far from whatever produced it. Non-finite generally: an
            // Infinity survives `l != l`, is stored, and later emerges as `Inf * 0.0` = NaN, which
            // MasterStage maps to -1.0, the exact full-scale click this guard prevents.
            delayL[delayPos] = l.finiteOrZero()
            delayR[delayPos] = r.finiteOrZero()
            delayPos = if (delayPos + 1 == delayFrames) 0 else delayPos + 1
            delayedLeft[i] = outL
            delayedRight[i] = outR
            left[i] = outL * gain
            right[i] = outR * gain
        }
    }

    /**
     * Per-sample envelope-follower + gain-curve step. Inlined into the classic loop of [process]
     * so that loop body stays specialized.
     * Uses precomputed [DB20_OVER_LN10] / [LN10_OVER_20] to skip per-sample `ln(10.0)` calls.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun envelopeStep(level: Double): Double {
        followEnvelope(level)

        return gainFor(calculateGainReduction(envelopeDb))
    }

    /**
     * One sample of the envelope follower: moves [envelopeDb] toward [level] (in dB). It reads no
     * gain-computer knob (threshold, ratio, knee), which is what lets [processGliding] move those
     * per sample over the one envelope.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun followEnvelope(level: Double) {
        // Same non-finite guard the lookahead path carries at `lookaheadStep`, and for a
        // sharper reason here: this path had NONE, so one +Inf sample latched the envelope
        // and silently DISABLED the limiter for good. `ln(Inf)` gives `envelopeDb = Inf`, the
        // next finite sample computes `Inf + releaseCoeff * -Inf` = NaN, and from then on
        // `calculateGainReduction(NaN)` is NaN, `NaN < GAIN_SKIP_THRESHOLD_DB` is false, and
        // this returns exactly 1.0 forever — a brickwall that has become a bit-exact
        // pass-through with nothing to indicate it. Note the direction: a NaN SAMPLE never
        // latched it (`NaN > SILENCE_LIN` is false); only +/-Inf did.
        val inputLevel = level.finiteOrZero()

        // Convert to dB (with silence floor to avoid log(0)).
        val inputDb = if (inputLevel > SILENCE_LIN) {
            DB20_OVER_LN10 * ln(inputLevel)
        } else {
            SILENCE_DB
        }

        // Envelope follower: one-pole IIR `y += k * (x - y)`. Instead of hard-switching the
        // coefficient on direction (a slope kink + chatter at every attack<->release crossing),
        // blend attack->release smoothly across a +/- ENV_COEFF_BLEND_DB window around error=0.
        // C1, branchless: error >= +W keeps full attackCoeff (peaks still caught at full speed →
        // limiter-safe), error <= -W is full releaseCoeff; |error| < W blends with no kink.
        val error = inputDb - envelopeDb
        val blend = smoothstep01((error + ENV_COEFF_BLEND_DB) / (2.0 * ENV_COEFF_BLEND_DB))
        val coeff = releaseCoeff + (attackCoeff - releaseCoeff) * blend
        envelopeDb += coeff * error
    }

    /**
     * Gain curve output (a reduction in dB, 0 or below) to a linear multiplier. Skip `exp` only
     * below an inaudible reduction floor (GAIN_SKIP_THRESHOLD_DB); the old -0.01 dB cutoff snapped
     * the gain 1.0<->0.99885.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun gainFor(gainReductionDb: Double): Double {
        return if (gainReductionDb < GAIN_SKIP_THRESHOLD_DB) {
            fastExp(gainReductionDb * LN10_OVER_20)
        } else {
            1.0
        }
    }

    /**
     * Per-sample lookahead gain: **min-hold → release → two cascaded boxes**.
     *
     * The ordering is load-bearing. Taking `min(smoothed, onePoleRelease)` *after* the smoother is
     * C0 at the crossover — the box path leaves its minimum with slope 0, accelerates, then meets
     * the release path and the gain slope drops ~40x in one sample. That is the same corner
     * [ENV_COEFF_BLEND_DB] exists to remove. Running release *before* the boxes keeps the whole
     * trajectory C1, and safety is preserved because `release <= held` pointwise, so
     * `box(release) <= box(held) <= required`.
     *
     * Why the ceiling is actually met: the min-hold window is `delayFrames + 1`, so the sample
     * emerging from the delay ring lies inside the hold window of **every** tap the smoother is
     * averaging. An average is >= its minimum, so the smoothed gain is <= what that sample requires.
     * Derivation in KLANG-00225 (master limiter lookahead) §2.3.
     *
     * **Inline on purpose** (engine follow-up item 3, 2026-10-10): called per sample, and its
     * bytecode is past V8's inlining limit, so as a plain function its level argument and its gain
     * result crossed a call V8 never inlines: one heap number per sample for the level whenever
     * anything sounds, one more for the gain from the first time the limiter reduces on, since its
     * release settles a few ulps below 1.0 and never returns to exactly 1.0 (about 2 and 4 KB per
     * block at the master on the production bundle). Inlined, nothing crosses. Its two callers each carry a
     * copy: the loop of [processLookahead] and the one-off pass of [reseedFromRing].
     * `audio/ref/performance.md`, the rule on a double handed to a function that is not inlined.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun lookaheadStep(inputLevel: Double): Double {
        // NaN-guard, and non-finite generally. NaN would poison the deque ordering (every
        // comparison false); +Infinity is worse — it drives `required` to 0, pins the gain there for
        // the whole window, and the master then fades the entire mix back in over ~450 ms. Reachable
        // in a raw engine via runaway feedback, and the DC blocker ahead of the limiter passes the
        // first Inf through unchanged. Same guard as the ring write, deliberately.
        val level = inputLevel.finiteOrZero()

        val inputDb = if (level > SILENCE_LIN) DB20_OVER_LN10 * ln(level) else SILENCE_DB
        val reductionDb = calculateGainReduction(inputDb)
        val required = if (reductionDb < GAIN_SKIP_THRESHOLD_DB) fastExp(reductionDb * LN10_OVER_20) else 1.0

        // ── Running MINIMUM over the next (delayFrames + 1) samples ──
        while (minTail != minHead) {
            val back = if (minTail == 0) minVals.size - 1 else minTail - 1
            if (minVals[back] >= required) minTail = back else break
        }
        minVals[minTail] = required
        minIdx[minTail] = sampleCounter
        minTail = if (minTail + 1 == minVals.size) 0 else minTail + 1
        // Wrap-safe form. The naive `minIdx[minHead] <= sampleCounter - (delayFrames + 1)` breaks
        // when sampleCounter overflows Int — ~12.4 h at 48 kHz, and it counts whenever the worklet
        // is alive, not only while playing. Past the wrap that predicate is true for nearly every
        // stored index, so the front walks past the tail and the loop can spin forever inside the
        // render callback. Subtracting first keeps the difference small and correct across the wrap.
        while (minHead != minTail && sampleCounter - minIdx[minHead] > delayFrames) {
            minHead = if (minHead + 1 == minVals.size) 0 else minHead + 1
        }
        val held = minVals[minHead]
        sampleCounter++

        // ── Dual release: slow branch absorbs the level, fast branch clears each hit ──
        // Slow: plain one-pole toward `held`, both directions. Never rises above 1 because `held`
        // never does.
        slowGain += releaseCoeff * (held - slowGain)

        // Fast: only the residual the slow branch has not taken. Instant down, one-pole up.
        // Fallback is `held`, not 1.0, so `slow * fast <= held` is unconditional: with slow <= 1,
        // `slow * min(held, fastRecovered) <= held` holds in the degenerate branch too. (1.0 is the
        // correct limit for the ordinary held >> slow case and gives the same answer there — but it
        // breaks the invariant under ~200 dB of sustained reduction, and an "always" that has an
        // exception is worse than the extra token.)
        val residual = if (slowGain > SILENCE_LIN) held / slowGain else held
        val fastRecovered = fastGain + fastReleaseCoeff * (1.0 - fastGain)
        fastGain = if (residual < fastRecovered) residual else fastRecovered

        // Product is <= held by construction, so the ceiling argument in the KDoc is unaffected.
        val releaseGain = slowGain * fastGain

        // ── Two cascaded box filters (C1 step response) ──
        // Each ring is written modulo its CAPACITY, and the sample leaving the window is the one
        // written `len` samples ago (`resizeSmoothing` says why). At a fixed length that is the same
        // value the old ring-of-`len` overwrote, added and subtracted in the same order: bit for bit.
        val len = boxLen
        val a = boxA
        val capacity = a.size
        val leavingA = boxAPos - len
        boxASum += releaseGain - a[if (leavingA < 0) leavingA + capacity else leavingA]
        a[boxAPos] = releaseGain
        boxAPos = if (boxAPos + 1 == capacity) 0 else boxAPos + 1
        val stage1 = boxASum / len

        val b = boxB
        val leavingB = boxBPos - len
        boxBSum += stage1 - b[if (leavingB < 0) leavingB + capacity else leavingB]
        b[boxBPos] = stage1
        boxBPos = if (boxBPos + 1 == capacity) 0 else boxBPos + 1

        return boxBSum / len
    }

    /** Prime the lookahead state to "wide open, silent rings". */
    private fun resetLookaheadState() {
        delayL.fill(0.0)
        delayR.fill(0.0)
        delayPos = 0
        resetLookaheadGain()
    }

    /** The detector half of [resetLookaheadState]: hold, release and boxes at rest, the ring untouched. */
    private fun resetLookaheadGain() {
        minHead = 0
        minTail = 0
        sampleCounter = 0
        slowGain = 1.0
        fastGain = 1.0
        primeBoxes()
        // Seed the deque with a unity entry so the first sample has a valid front.
        minVals[0] = 1.0
        minIdx[0] = 0
        minTail = 1
    }

    /** Clamped cubic smoothstep: 0 below 0, 1 above 1, C1 at both ends. Polynomial (no `exp`). */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun smoothstep01(x: Double): Double {
        val c = x.coerceIn(0.0, 1.0)
        return c * c * (3.0 - 2.0 * c)
    }

    /**
     * Calculate gain reduction in dB for a given input level in dB.
     * Implements soft-knee compression — parabolic blend on `[-halfKnee, +halfKnee]`,
     * verified C¹ continuous at both boundaries when `kneeDb > 0`.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun calculateGainReduction(inputDb: Double): Double =
        gainReductionDb(
            inputDb = inputDb,
            thresholdDb = thresholdValue,
            slope = 1.0 / ratioValue - 1.0,
            kneeDb = kneeValue,
        )

    /**
     * [calculateGainReduction] with the knobs passed in: the one home of the curve, shared by the
     * settled path (the fields) and [processGliding] (the knobs of each sample). The ratio arrives as
     * the curve's SLOPE, `1 / ratio - 1` (0 at 1:1, -1 at infinity:1), because that is the quantity
     * the curve is linear in, and so the one a glide must move linearly.
     */
    private fun gainReductionDb(inputDb: Double, thresholdDb: Double, slope: Double, kneeDb: Double): Double {
        val overshootDb = inputDb - thresholdDb
        val halfKnee = kneeDb / 2.0

        return when {
            // Below threshold - no compression
            overshootDb < -halfKnee -> 0.0

            // In the knee - soft transition
            overshootDb < halfKnee -> {
                slope * (overshootDb + halfKnee) * (overshootDb + halfKnee) / (2.0 * kneeDb)
            }

            // Above threshold - full compression
            else -> slope * overshootDb
        }
    }

    /**
     * Update time constants for attack and release.
     */
    private fun updateCoefficients() {
        val attackTime = max(MIN_TIME_SECONDS, attackValue)
        val releaseTime = max(MIN_TIME_SECONDS, releaseValue)

        attackCoeff = timeConstantCoeff(timeSeconds = attackTime, sampleRate = sampleRate.toDouble())
        releaseCoeff = timeConstantCoeff(timeSeconds = releaseTime, sampleRate = sampleRate.toDouble())
        // Fast branch of the dual release (lookahead path only). A tenth of the configured release,
        // so `releaseSeconds` keeps meaning "the release" and no new knob is needed.
        fastReleaseCoeff = timeConstantCoeff(timeSeconds = releaseTime / FAST_RELEASE_DIVISOR, sampleRate = sampleRate.toDouble())
    }

    /**
     * Re-seeds the lookahead detector from what the ring holds now, under the knobs in force, and
     * leaves the ring (the signal) untouched: the detector goes to rest and then hears the ring's
     * [latencyFrames] samples, oldest first. The instance is then in the state of a FRESH one that
     * has been fed exactly those samples, so the ceiling argument of [lookaheadStep] holds from the
     * next sample it emits, as it does for a fresh instance.
     *
     * For an orbit compressor that switches back on after an off stretch with its ring running
     * (`KatalystCompressorEffect`): without it the new life starts from whatever the detector last
     * computed, at other knobs. A one-off pass of [latencyFrames] detector steps (at most
     * [MAX_LOOKAHEAD_SECONDS] of frames), for a switch edge, never per block. Allocates nothing. A no-op without a
     * lookahead.
     */
    internal fun reseedFromRing() {
        if (delayFrames <= 0) {
            return
        }

        resetLookaheadGain()

        var p = delayPos

        repeat(delayFrames) {
            lookaheadStep(max(abs(delayL[p]), abs(delayR[p])))
            p = if (p + 1 == delayFrames) 0 else p + 1
        }
    }

    /**
     * Reset the compressor state.
     */
    fun reset() {
        envelopeDb = SILENCE_DB
        if (delayFrames > 0) resetLookaheadState()
    }

    companion object {
        // Compile-time constants for the dB↔linear math — saves one `ln(10.0)` per sample
        // in the hot loop. Full elimination of `exp`/`ln` would require a linear-domain
        // rewrite (deferred — see file KDoc).
        private const val DB20_OVER_LN10: Double = 8.685889638065035   // 20 / ln(10)
        private const val LN10_OVER_20: Double = 0.11512925464970229   // ln(10) / 20

        /**
         * Below this many frames the smoother degenerates (two boxes need >= 1 tap each) and the
         * gain would step rather than ramp — a click. Sub-minimum lookahead takes the classic path.
         */
        const val MIN_LOOKAHEAD_FRAMES: Int = 8

        /**
         * Ceiling on a lookahead a user can ask for. Chains are built on the audio thread (the
         * orbit's `KatalystChainBuilder`, the master's `MasterBus.register`), so an unbounded value
         * would allocate there. At 50 ms and 48 kHz the stereo ring is 2400 frames of 16 bytes,
         * about 38 KB, and the whole lookahead state (ring, hold deque, boxes) about 86 KB; 10 s
         * would be about 17 MB. A resource bound, not a taste clamp.
         */
        const val MAX_LOOKAHEAD_SECONDS: Double = 0.05

        /**
         * The one coercion of a user's lookahead, in seconds: a non-finite or negative value is
         * 0 (no lookahead, the classic path), and anything above [MAX_LOOKAHEAD_SECONDS] is that
         * ceiling. A negative size would throw on the audio thread and Infinity would ask for
         * `Int.MAX_VALUE` doubles, so every build path that takes a lookahead from a DSL goes
         * through here.
         */
        fun coerceLookaheadSeconds(seconds: Double): Double =
            if (seconds.isFinite()) seconds.coerceIn(0.0, MAX_LOOKAHEAD_SECONDS) else 0.0

        /** The slots of [glideArgs]: the threshold's ramp from and to, the inverse ratio's, the knee's. */
        internal const val GLIDE_THRESHOLD_FROM: Int = 0
        internal const val GLIDE_THRESHOLD_TO: Int = 1
        internal const val GLIDE_INVERSE_RATIO_FROM: Int = 2
        internal const val GLIDE_INVERSE_RATIO_TO: Int = 3
        internal const val GLIDE_KNEE_FROM: Int = 4
        internal const val GLIDE_KNEE_TO: Int = 5
        private const val GLIDE_ARGS: Int = 6

        /** The slots of [knobArgs]: the threshold, the ratio, the knee, the attack and the release. */
        internal const val KNOB_THRESHOLD: Int = 0
        internal const val KNOB_RATIO: Int = 1
        internal const val KNOB_KNEE: Int = 2
        internal const val KNOB_ATTACK: Int = 3
        internal const val KNOB_RELEASE: Int = 4
        private const val KNOB_ARGS: Int = 5

        /**
         * The shortest attack or release time the follower computes its coefficient from, in seconds: a bound against a
         * defect (a zero or negative time has no one-pole), not a clamp on taste; the stored knob is untouched. The
         * orbit compressor's knob move releases with the same floor (`KatalystCompressorEffect`).
         */
        internal const val MIN_TIME_SECONDS: Double = 0.0001

        /**
         * The input level, in dBFS, at which the orbit compressor's knob move judges a new setting ([reducesLessAtGlideEnds]):
         * a move that reduces less HERE releases, any other glides. A fixed loud level, decided by the maintainer on
         * 2026-10-17 ("judge at a fixed loud level"): loud material is where a swell and a ceiling are heard. The
         * follower's envelope was tried first and failed: after a rest it has fallen toward -100 dB, every relaxing move
         * read as a tie and glided in 50 ms, and the new owner's first note met the old threshold moving linearly (a 16 dB
         * swell in 20 ms). A crossover, threshold and ratio moving in opposite directions, is judged by its behaviour on
         * loud material; a quiet passage may then see the other direction for a moment, bounded by the two curves'
         * difference at that level.
         */
        internal const val KNOB_MOVE_REFERENCE_DB: Double = 0.0

        /** How much faster the dual release's fast branch is than the configured release. */
        private const val FAST_RELEASE_DIVISOR: Double = 10.0

        /** Fewest taps per box filter. Two 1-tap boxes are an identity — see `resizeSmoothing`. */
        private const val MIN_BOX_TAPS: Int = 4

        // Envelope follower silence floor.
        private const val SILENCE_DB: Double = -100.0
        private const val SILENCE_LIN: Double = 1e-10

        // Attack<->release coefficient blend half-width in dB. The follower coefficient
        // smoothstep-blends from releaseCoeff to attackCoeff across [-W, +W] around error=0,
        // removing the hard direction-switch kink. Larger = smoother but softer near-threshold
        // attack; tune by ear.
        private const val ENV_COEFF_BLEND_DB: Double = 2.0

        // Gain-curve exp-skip cutoff in dB. Below this reduction we return 1.0 to skip the
        // per-sample `exp`; small enough that exp(cutoff) ≈ 1 within ~-100 dB so the residual
        // step is inaudible (the old -0.01 dB cutoff snapped the gain by ~0.12%).
        private const val GAIN_SKIP_THRESHOLD_DB: Double = -1e-4

        /** Returns [value] when finite; otherwise [fallback]. Used to keep param setters safe against NaN/Inf. */
        private fun guardOr(value: Double, fallback: Double): Double =
            if (value.isFinite()) value else fallback

    }

}
